import type { ReactNode, CSSProperties } from 'react';

import { motion } from 'framer-motion';
import { useChildMatches } from '@tanstack/react-router';

import { motion as motionTokens } from '../../theme/motion';

// ----------------------------------------------------------------------

export type RouteTransitionProps = {
  children: ReactNode;
  style?: CSSProperties;
};

/**
 * A short fade and rise for a layout's `<Outlet>` on each route change.
 * Keyed on the match the outlet draws, not the leaf, so a nested route below it
 * does not remount the page. It changes when the new content commits, not when
 * navigation starts, so a lazy route does not replay the old page.
 */
export function RouteTransition({ children, style }: RouteTransitionProps) {
  const matchId = useChildMatches({ select: (matches) => matches[0]?.id ?? '' });
  return (
    <motion.div
      key={matchId}
      style={style}
      initial={{ opacity: 0, y: 8, filter: 'blur(4px)' }}
      // A filter, even blur(0px), makes the page the box of every fixed element in it. It goes at the end.
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)', transitionEnd: { filter: 'none' } }}
      transition={{ duration: motionTokens.durationMs.base / 1000, ease: motionTokens.easeArray.out }}
    >
      {children}
    </motion.div>
  );
}
